import React from "react";
import { Col, Grid, Divider, Row, Space, Tag, Table } from "antd";
import ViewableResponse from "../../../modules/Inference/components/ViewableResponse";
import { OpenAIOutlined } from "@ant-design/icons";
import { PieChart, TableOfContents } from "lucide-react";
import NivoDonut from "../../../modules/Charts/Nivo/NivoDonut";
import PnL from "../../../components/PnL";
import TimeCover from "../../Experiments/TimeCover";
import { useBlotterSnapshot } from "./useBlotter";


const SnapshotTickers = ({ tickers = [] }) => {
  const sortedTickers = tickers
    .filter(ticker => ticker.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {sortedTickers.map((ticker, index) => (
          <div
            key={ticker.id}
            className="bg-white px-3 py-1.5 shadow-sm rounded-sm flex flex-col items-start space-y-1"
          >
            <div className="flex items-center space-x-1 w-full">
              <span className="text-slate-600 text-xs">
                {index + 1})
              </span>
              <span className="text-blue-600 cursor-pointer text-xs">
                {ticker.id}
              </span>
            </div>
            <div className="text-sm font-medium">
              {typeof ticker.value === 'number' ? ticker.value.toFixed(2) : '0.00'}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BlotterSnapshot = ({ data, colorMap, startDate }) => {
  const { pieData, endDate, markdownTextBody, returnsArray } = useBlotterSnapshot({
    data,
    colorMap,
    startDate,
  });


  // Split the holdings into top/bottom half for display.
  const half = Math.ceil(pieData.length / 2);
  const topHoldings = pieData.slice(0, half);
  const bottomHoldings = pieData.slice(half);
  const columns = [
    {
      title: "Ticker",
      dataIndex: "id",
      key: "id",
        render: (ticker, record) => <span className={record.value > 0 ? '' : 'line-through'}>
            {ticker?.toUpperCase()}
        </span>
    },
    {
      title: "Value (%)",
      dataIndex: "value",
      key: "value",
      render: (val) => <span>
          {val > 0 ? `${val.toFixed(2)}%` : '--'}
      </span>
    },
  ];

  // Popover content: 800px tall x 500px wide
  const popoverContent = (
    <div style={{ width: 500, height: 800, padding: 16 }}>
      {/* 400×400 Chart Wrapper */}
      <TimeCover date={endDate} />
      {/* Holdings List */}
        <Row>
            <Col span={12}>
                <Table
                  columns={columns}
                  dataSource={topHoldings}
                  pagination={false}
                  scroll={{ y: 400 }}
                  size="small"
                  rowKey="id"
                />
              </Col>
              <Col span={12}>
                <Table
                  columns={columns}
                  dataSource={bottomHoldings}
                  pagination={false}
                  scroll={{ y: 400 }}
                  size="small"
                  rowKey="id"
                />
              </Col>
        </Row>
    </div>
  );

  return (
      <div>
        <Row justify={'space-between'} align={'top'} className={'pr-6 border-b border-gray-200 pb-4'}>
            <div className={'flex flex-col'}>
              <div>
                <span className={'text-2xl font-semibold text-center'}>
                  <OpenAIOutlined/> {data?.text?.title}
                </span>
              </div>
            </div>
            {/*<div>*/}
            {/*  <TimeCover date={endDate} size={'small'}/>*/}
            {/*</div>*/}
        </Row>
        <Row gutter={16} wrap={false} align={'top'} justify={'space-between'} className={'pt-6'}>
          <Col>
            <ViewableResponse
              markdownText={markdownTextBody}
            />
            {Array.isArray(returnsArray) && returnsArray.length > 0 && (
              <Space className="px-6">
                {returnsArray.map(([ticker, returns]) => (
                  <Tag
                    key={ticker}
                    bordered={false}
                    color="blue"
                    style={{ fontSize: 16, borderRadius: 8 }}
                    className="px-3 py-2 shadow-sm"
                  >
                    {ticker?.split("_")?.[0]?.toUpperCase?.()}: <PnL value={returns} />
                  </Tag>
                ))}
              </Space>

            )}

          </Col>
          <Col>
            <div className={'py-6'}>
              <div className={'px-4'}>
                <SnapshotTickers tickers={pieData}/>
              </div>
              <div style={{width: 350, height: 350, margin: "0 auto"}}>
                <NivoDonut
                    data={pieData}
                    showLegendValues
                    showLabels
                    radius={0.6}
                    centerText={endDate}
                    showLegend={false}
                />
              </div>
            </div>
          </Col>
        </Row>

      </div>
  );
};

export default BlotterSnapshot;
